import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { CalendarDays, CheckCircle2, XCircle, HandHeart } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR");

/** "Agendadas × Realizadas" — execution rate panel. */
export default function AgendadasRealizadasCard({ data }) {
  const realized = data?.cases_realized ?? 0;
  const suspended = data?.cases_suspended ?? 0;
  const proBono = data?.cases_proBono ?? 0;
  const scheduled = realized + suspended;
  const rate = scheduled > 0 ? Math.round((realized / scheduled) * 1000) / 10 : 0;
  const realizedPct = scheduled > 0 ? (realized / scheduled) * 100 : 0;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="agendadas-realizadas-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-blue-700" /> Agendadas × Realizadas
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Taxa de execução real da equipe. Pro Bono contam em produção mas não geram faturamento.
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-slate-500">Taxa de execução</div>
            <div className="font-display text-3xl font-bold text-emerald-700" data-testid="execution-rate">
              {rate.toFixed(1).replace(".", ",")}%
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Link to="/casos" className="border border-slate-200 rounded-md p-3 hover:border-blue-400 hover:bg-blue-50/40 transition-colors" data-testid="kpi-scheduled">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
              <CalendarDays className="h-3.5 w-3.5" /> Agendadas
            </div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1">{fmt(scheduled)}</div>
          </Link>
          <Link to="/casos" className="border border-emerald-200 bg-emerald-50/30 rounded-md p-3 hover:border-emerald-400 hover:bg-emerald-50/60 transition-colors" data-testid="kpi-realized">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5" /> Realizadas
            </div>
            <div className="font-display text-2xl font-bold text-emerald-700 mt-1">{fmt(realized)}</div>
          </Link>
          <Link to="/casos?status=suspensa" className="border border-amber-200 bg-amber-50/30 rounded-md p-3 hover:border-amber-400 hover:bg-amber-50/60 transition-colors" data-testid="kpi-suspended">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-800">
              <XCircle className="h-3.5 w-3.5" /> Suspensas
            </div>
            <div className="font-display text-2xl font-bold text-amber-700 mt-1">{fmt(suspended)}</div>
          </Link>
          <Link to="/casos?pro_bono=true" className="border border-emerald-200 bg-emerald-50/30 rounded-md p-3 hover:border-emerald-400 hover:bg-emerald-50/60 transition-colors" data-testid="kpi-pro-bono" title="Cirurgias pro bono — entram na produção mas não geram faturamento.">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-emerald-800">
              <HandHeart className="h-3.5 w-3.5" /> Pro Bono
            </div>
            <div className="font-display text-2xl font-bold text-emerald-700 mt-1">{fmt(proBono)}</div>
          </Link>
        </div>

        {scheduled > 0 && (
          <div>
            <div className="h-3 w-full rounded-full overflow-hidden bg-slate-100 flex" data-testid="progress-bar">
              <div className="bg-emerald-600 transition-all" style={{ width: `${realizedPct}%` }} title={`${realized} realizada(s)`} />
              <div className="bg-amber-500 transition-all" style={{ width: `${100 - realizedPct}%` }} title={`${suspended} suspensa(s)`} />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-emerald-600" /> Realizadas</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> Suspensas</span>
              </div>
              <Link to="/casos?status=suspensa" className="text-blue-700 hover:underline font-medium">
                Ver suspensas →
              </Link>
            </div>
          </div>
        )}
        {scheduled === 0 && (
          <div className="text-sm text-slate-500 py-4 text-center">
            Sem cirurgias agendadas no período ainda.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
